import { supabase } from '@/lib/supabase';
import { currentUserId } from '@/lib/session';
import {
  userSettingsRowFromJson,
  USER_SETTINGS_COLUMNS,
  USER_SETTINGS_LIVE_OPTIONAL_COLUMNS,
  USER_SETTINGS_V181_COLUMNS,
  type UserSettingsRow,
} from '@/types';
import { isMissingColumn } from './missingColumn';
import { LIVE_OPTIONAL_COLUMNS, V181_COLUMNS, withoutMissing } from '@/lib/settingsSync';

// Port of lib/repositories/user_settings_repository.dart — CRUD for the
// `user_settings` table. The client supplies `user_id` explicitly because
// Kong doesn't reliably forward the JWT to PostgREST on this stack; RLS is
// owner-only (`user_owns_settings`, user_id = auth.uid(), since 171) and the
// explicit filter keeps every query on the caller's own row.
//
// Settings sync contract §1/§6: the optional columns are read in tiers so a
// server without migration 181 (or, defensively, without 160/175) still
// syncs everything it has. A missing tier is remembered for the page load —
// no retry storm — and its settings stay on this device.

const V181 = 'v181';
const LIVE = 'live';
/** Column groups this server doesn't have (cached per page load). */
const missingGroups = new Set<string>();

/** For tests / after a migration lands without a reload. */
export function resetUserSettingsCapabilities(): void { missingGroups.clear(); }

/** Columns the server is known not to have right now. */
export function missingSettingsColumns(): Set<string> {
  const out = new Set<string>();
  if (missingGroups.has(V181)) V181_COLUMNS.forEach((c) => out.add(c));
  if (missingGroups.has(LIVE)) LIVE_OPTIONAL_COLUMNS.forEach((c) => out.add(c));
  return out;
}

function selectList(): string {
  let cols = USER_SETTINGS_COLUMNS;
  if (!missingGroups.has(LIVE)) cols += `, ${USER_SETTINGS_LIVE_OPTIONAL_COLUMNS}`;
  if (!missingGroups.has(V181)) cols += `, ${USER_SETTINGS_V181_COLUMNS}`;
  return cols;
}

/** Marks the newest optional tier THIS select included as missing; false
 *  when it included none. Keyed on the select that failed, so two fetches
 *  failing at the same time (profile + settings sync on sign-in) don't
 *  each drop a tier and wrongly lose the live 160/175 columns. */
function dropTier(select: string): boolean {
  if (select.includes(USER_SETTINGS_V181_COLUMNS)) { missingGroups.add(V181); return true; }
  if (select.includes(USER_SETTINGS_LIVE_OPTIONAL_COLUMNS)) { missingGroups.add(LIVE); return true; }
  return false;
}

export const userSettingsRepository = {
  /** The signed-in user's id (undefined when signed out). */
  async signedInUserId(): Promise<string | undefined> {
    return currentUserId();
  },

  /** Fetches the signed-in user's row, or null when none exists yet. */
  async fetch(): Promise<UserSettingsRow | null> {
    const uid = await currentUserId();
    if (!uid) return null;
    for (;;) {
      const select = selectList();
      const { data, error } = await supabase
        .from('user_settings')
        .select(select)
        .eq('user_id', uid)
        .maybeSingle();
      if (error) {
        if (isMissingColumn(error) && dropTier(select)) continue;
        throw error;
      }
      if (!data) return null;
      return userSettingsRowFromJson(data as unknown as Record<string, unknown>);
    }
  },

  /**
   * Writes only the given columns (an upsert with just these keys: PostgREST
   * updates exactly the columns present, so a patch never touches another
   * setting — fixes the stale-bundle overwrite, audit C1). Columns the server
   * doesn't have are stripped; when the write fails on one, the tier is
   * remembered and the rest is retried. Returns the columns that were NOT
   * stored because the server lacks them (they stay on this device).
   */
  async patch(fields: Record<string, unknown>): Promise<string[]> {
    const uid = await currentUserId();
    if (!uid) throw new Error('No signed-in user.');
    for (;;) {
      const missing = missingSettingsColumns();
      const body = withoutMissing(fields, missing);
      const skipped = Object.keys(fields).filter((k) => missing.has(k));
      if (Object.keys(body).length === 0) return skipped;
      const { error } = await supabase
        .from('user_settings')
        .upsert({ user_id: uid, ...body }, { onConflict: 'user_id' });
      if (!error) return skipped;
      if (isMissingColumn(error)) {
        // Blame the newest tier THIS body carried (181 before 160/175). The
        // body was stripped before the await, so a tier a concurrent write
        // marked missing meanwhile must not shift the blame to the next one.
        const keys = Object.keys(body);
        const has = (cols: readonly string[]) => keys.some((k) => cols.includes(k));
        if (has(V181_COLUMNS)) { missingGroups.add(V181); continue; }
        if (has(LIVE_OPTIONAL_COLUMNS)) { missingGroups.add(LIVE); continue; }
      }
      throw error;
    }
  },

  /** Persists only the typing-drill language pair columns. */
  async upsertTypingDrillPair(sourceLang: string, targetLang: string): Promise<void> {
    await this.patch({ typing_drill_source_lang: sourceLang, typing_drill_target_lang: targetLang });
  },

  /** Writes the profile fields; stamps onboarded_at when `onboardedNow`. */
  async upsertProfile(args: {
    displayName: string | null;
    avatarColor: number | null;
    nativeLanguage: string | null;
    learningLanguage: string | null;
    onboardedNow?: boolean;
  }): Promise<void> {
    const uid = await currentUserId();
    if (!uid) return;
    const payload: Record<string, unknown> = {
      user_id: uid,
      display_name: args.displayName,
      avatar_color: args.avatarColor,
      native_language: args.nativeLanguage,
      learning_language: args.learningLanguage,
    };
    if (args.onboardedNow) payload['onboarded_at'] = new Date().toISOString();
    const { error } = await supabase
      .from('user_settings')
      .upsert(payload, { onConflict: 'user_id' });
    if (error) throw error;
  },
};
